import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserPlus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Team() {
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", email: "", password: "", company_id: "" });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [m, c] = await Promise.all([api.get("/team"), api.get("/companies")]);
        setMembers(m.data);
        setCompanies(c.data);
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/team", { ...form, role: "team_lead" });
            toast.success("Team lead added");
            setForm({ name: "", email: "", password: "", company_id: "" });
            await load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this team member?")) return;
        try {
            await api.delete(`/team/${id}`);
            toast.success("Removed");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="p-8 lg:p-10 max-w-[1400px]" data-testid="team-page">
            <div className="mb-8">
                <div className="overline mb-2">Access control</div>
                <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">TEAM</h1>
                <p className="text-sm text-muted2 mt-2 max-w-xl">Owners see everything. Team leads only see and report for their assigned company.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 grid-card overflow-hidden">
                    {loading ? (
                        <div className="p-6 overline">Loading…</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-paper border-b border-rule text-left">
                                <tr>
                                    <th className="overline px-4 py-3">Name</th>
                                    <th className="overline px-4 py-3">Email</th>
                                    <th className="overline px-4 py-3">Role</th>
                                    <th className="overline px-4 py-3">Company</th>
                                    {user?.role === "owner" ? <th className="overline px-4 py-3"></th> : null}
                                </tr>
                            </thead>
                            <tbody>
                                {members.map((m) => {
                                    const c = companies.find((x) => x.id === m.company_id);
                                    return (
                                        <tr key={m.id} className="border-t border-rule" data-testid={`member-row-${m.id}`}>
                                            <td className="px-4 py-3 font-medium">{m.name}</td>
                                            <td className="px-4 py-3 text-muted2">{m.email}</td>
                                            <td className="px-4 py-3">
                                                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${m.role === "owner" ? "border-klein text-klein" : "border-ink text-ink"}`}>
                                                    {m.role.replace("_", " ")}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-muted2">{c?.name || "—"}</td>
                                            {user?.role === "owner" ? (
                                                <td className="px-4 py-3 text-right">
                                                    {m.role !== "owner" && (
                                                        <button onClick={() => remove(m.id)} data-testid={`remove-${m.id}`}
                                                            className="text-neg hover:underline text-xs inline-flex items-center gap-1">
                                                            <Trash size={12} /> Remove
                                                        </button>
                                                    )}
                                                </td>
                                            ) : null}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {user?.role === "owner" ? (
                    <form onSubmit={submit} className="grid-card p-6 space-y-3 h-fit" data-testid="add-member-form">
                        <div className="overline">Add team lead</div>
                        <input
                            data-testid="member-name"
                            type="text" placeholder="Full name" required
                            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="input-base"
                        />
                        <input
                            data-testid="member-email"
                            type="email" placeholder="email@example.com" required
                            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                            className="input-base"
                        />
                        <input
                            data-testid="member-password"
                            type="text" placeholder="Initial password (min 6)" required minLength={6}
                            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                            className="input-base"
                        />
                        <select
                            data-testid="member-company"
                            value={form.company_id}
                            onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                            required
                            className="input-base"
                        >
                            <option value="">— Assign company —</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        <button type="submit" disabled={busy} data-testid="member-submit"
                            className="w-full bg-ink text-paper py-2.5 rounded-sm font-medium text-sm hover:bg-klein transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                            <UserPlus size={14} /> {busy ? "Adding…" : "Add team lead"}
                        </button>
                    </form>
                ) : (
                    <div className="grid-card p-6 text-sm text-muted2 h-fit">
                        Only the owner can add or remove team members.
                    </div>
                )}
            </div>
        </div>
    );
}
